import moment from 'moment-timezone';
import { DateTime } from 'luxon';
import _ from 'lodash';
import {
  ARROW_TYPES,
  CaseStatus,
  CaseStatusFilter,
  CaseStatusRDNMatch,
  CaseType,
  DAILY_REPORT_HOUR,
  TargetRecoveryRateDuration,
  calculatePercentage,
} from '@rapid-recovery-agency-inc/insightt-shared';
import { CustomUser, DrnCameraDataQuery, RdnCase } from '@rapid-recovery-agency-inc/insightt-graphql';
import { OrderTypeCategory } from '@rapid-recovery-agency-inc/insightt-graphql';
import { DateRangeType } from '@rapid-recovery-agency-inc/sloth-ui-web';

import { DateRangeSelector, MONTH_PICKER_FORMAT } from '../../constants/app.constants';
import { OrderType } from '../../shared/generated-types/graphql';
import { getDateForCalculation } from '../../shared/utils';
import { useAppSelector } from '../../redux/store';
import { dateCalculation } from '../overview/missed-repossessions/helpers';
import { DayMenuOption } from './reports-constants';
import { CameraCarsStatisticsResult } from './types';

export type DateRangeSelectorType = keyof typeof DateRangeSelector;

export const toMonthArray = (
  arr: Array<{ [key: string]: string | number }> = [],
  key: string = 'month',
): number[] => {
  const months = Array<number>(12);

  arr.forEach((item) => {
    const keyValue = item[key];
    const monthStr = typeof keyValue === 'string' ? keyValue : String(keyValue);
    const idx = Number(monthStr.split('-')[1]) - 1;

    if (idx >= 0 && idx < 12) {
      months[idx] = Number(item.count);
    }
  });

  return months;
};

export const areCoordinatesNullMissedRepo = (caseData: RdnCase) => {
  if (caseData?.status === CaseStatusRDNMatch.REPOSSESSED && caseData?.rdnRepossession?.lat) {
    return false;
  }

  if (caseData?.status !== CaseStatusRDNMatch.REPOSSESSED && caseData?.spottedLat) {
    return false;
  }

  return true;
};

export const getAddressMissedRepo = (caseData: RdnCase) => {
  if (caseData?.status !== CaseStatusRDNMatch.REPOSSESSED) {
    return caseData?.spottedAddress;
  }

  if (caseData?.status === CaseStatusRDNMatch.REPOSSESSED && caseData?.rdnRepossession?.address) {
    return caseData?.rdnRepossession?.address;
  }

  if (caseData?.status === CaseStatusRDNMatch.REPOSSESSED) {
    return caseData?.rdnRepossession?.address;
  }

  return '';
};

export const getDailyReportDate = (daySelected: DayMenuOption) => {
  if (daySelected === DayMenuOption.YESTERDAY) {
    return DateTime.now().minus({ days: 1 }).toFormat('yyyy-MM-dd');
  }
  return DateTime.now().toFormat('yyyy-MM-dd');
};

export const toServiceBranchId = (branchId: string) => {
  if (branchId === null || branchId === undefined) throw new Error('toServiceBranchId: Invalid branchId');
  if (String(branchId) === '0') {
    return undefined;
  }
  if (String(branchId) === '-1') {
    return null;
  }
  return Number(branchId);
};

export const toServiceBranchIdTRR = (branchId: string) => {
  if (branchId === null || branchId === undefined) throw new Error('toServiceBranchId: Invalid branchId');
  if (String(branchId) === '0') {
    return null;
  }
  return Number(branchId);
};

export const getNoDataMessages = (dateSelector: DateRangeSelectorType, customDate: string) => {
  const currentDate = moment().format(MONTH_PICKER_FORMAT);
  const waitForDataMessage =
    dateSelector === DateRangeSelector.CUSTOM &&
    moment(customDate, MONTH_PICKER_FORMAT).isBefore(moment(currentDate, MONTH_PICKER_FORMAT))
      ? ''
      : ' Please wait until there is data available.';
  const noDataMessage =
    dateSelector === DateRangeSelector.YTD
      ? 'There is no data to show for this year.'
      : 'There is no data to show for this month.';

  return { waitForDataMessage, noDataMessage };
};

export const getDateRange = (selector: DateRangeSelectorType, timezone: string, customDate: string) => {
  if (selector === DateRangeSelector.MTD) {
    return {
      startDate: moment().startOf('month').tz(timezone, true),
      endDate: moment().endOf('day').tz(timezone, true),
    };
  }
  if (selector === DateRangeSelector.YTD) {
    return {
      startDate: moment().startOf('year').tz(timezone, true),
      endDate: moment().endOf('day').tz(timezone, true),
    };
  }
  if (selector === DateRangeSelector.CUSTOM) {
    const currentDate = moment();
    const customDateMoment = moment(customDate, MONTH_PICKER_FORMAT);
    const startDate = customDateMoment.clone().startOf('month').tz(timezone, true);
    // If the custom date is the current month, use the current date as the end date
    const endDate =
      moment(currentDate).format(MONTH_PICKER_FORMAT) === customDateMoment.format(MONTH_PICKER_FORMAT)
        ? currentDate.clone().endOf('day').tz(timezone, true)
        : customDateMoment.clone().endOf('month').tz(timezone, true);
    return { startDate, endDate };
  }
  throw new Error(`Invalid selector: ${selector}`);
};

export const getClientIdForTargetRecoveryRate = (lenderId: string | null, lienholderId: string | null) => {
  if (lenderId && lienholderId) {
    return `${lenderId}-${lienholderId}`;
  }

  if (!lenderId && lienholderId) {
    return lienholderId;
  }

  return lenderId;
};

interface SeparateTRRItemsInput {
  branchId?: string | number | null;
  clientId?: string | null;
  duration: TargetRecoveryRateDuration | string;
  targetRecoveryRate: number;
  userId: number;
  orderType?: OrderType;
}
export const separateTRRItems = (
  formattedInput: SeparateTRRItemsInput[],
  lenderTRRForUser: SeparateTRRItemsInput[],
) => {
  const trrInDB: SeparateTRRItemsInput[] = [];
  const trrInDBNull: SeparateTRRItemsInput[] = [];
  const trrNotInDB: SeparateTRRItemsInput[] = [];

  for (let i = 0; i < formattedInput.length; i++) {
    const inputItem = formattedInput[i];
    const branchId = String(inputItem.branchId) === '0' ? null : inputItem.branchId;
    inputItem.branchId = branchId;

    // Check if an item in formattedInput exists in lenderTRRForUser
    const isInDB = lenderTRRForUser.some(
      (dbItem) =>
        dbItem.clientId === inputItem.clientId &&
        dbItem.userId === inputItem.userId &&
        dbItem.branchId === branchId &&
        dbItem.orderType === inputItem.orderType &&
        dbItem.duration === inputItem.duration,
    );

    if (isInDB) {
      if (branchId === null) {
        trrInDBNull.push(inputItem);
        continue;
      }
      trrInDB.push(inputItem);
    } else {
      trrNotInDB.push(inputItem);
    }
  }

  return { trrInDB, trrInDBNull, trrNotInDB };
};

type SeparateLienholderTRRItemsInput = {
  clientId?: string | null;
  userId: string | number;
  branchId?: string | number | null;
  duration: string;
  lienholderId?: string;
  // ... include any other properties that are part of the input
};
export const separateLienholderTRRItems = (
  formattedInput: SeparateLienholderTRRItemsInput[],
  lenderTRRForUser: SeparateLienholderTRRItemsInput[],
) => {
  const trrInDB: SeparateLienholderTRRItemsInput[] = [];
  const trrInDBNull: SeparateLienholderTRRItemsInput[] = [];
  const trrNotInDB: SeparateLienholderTRRItemsInput[] = [];

  for (let i = 0; i < formattedInput.length; i++) {
    const inputItem = formattedInput[i];
    const branchId = String(inputItem.branchId) === '0' ? null : inputItem.branchId;
    inputItem.branchId = branchId;

    // Check if an item in formattedInput exists in lenderTRRForUser
    const isInDB = lenderTRRForUser.some(
      (dbItem) =>
        (inputItem.clientId === undefined || dbItem.clientId === inputItem.clientId) &&
        dbItem.userId === inputItem.userId &&
        dbItem.branchId === branchId &&
        dbItem.duration === inputItem.duration &&
        dbItem.lienholderId === inputItem.lienholderId,
    );

    if (isInDB) {
      if (branchId === null) {
        trrInDBNull.push(inputItem);
      } else {
        trrInDB.push(inputItem);
      }
    } else {
      trrNotInDB.push(inputItem);
    }
  }

  return { trrInDB, trrInDBNull, trrNotInDB };
};

export const getTRRGoal = (currentRecoveryRate: number, targetRecoveryRate: number) => {
  if (targetRecoveryRate === 0) return 0;

  return calculatePercentage(currentRecoveryRate, targetRecoveryRate);
};

export const getFirstNameLastNameDRN = (user: CustomUser) => {
  if (!user.name)
    return {
      firstName: '',
      lastName: '',
    };

  if (user.name.includes('DRN ID: ')) {
    const splittedName = user.name.split('DRN ID: ');
    return {
      firstName: splittedName[0],
      lastName: splittedName[1],
    };
  }

  const splittedName = user.name.split(' ');
  return {
    firstName: splittedName[0],
    lastName: splittedName[1],
  };
};

export const formatTopCameraCardsStatistics = (
  input?: DrnCameraDataQuery['drnCameraDataByUserList']['statistics'],
): CameraCarsStatisticsResult => {
  const defaultAverageObject = {
    __typename: 'CompanyWideAgentMetric' as const,
    total: 0,
    totalArrowType: ARROW_TYPES.neutral,
    average: 0,
    averageArrowType: ARROW_TYPES.neutral,
  };

  if (!input) {
    return {
      scannedAverageAndTotal: defaultAverageObject,
      allHitsAverageAndTotal: defaultAverageObject,
      securedAverageAndTotal: defaultAverageObject,
    };
  }

  return {
    scannedAverageAndTotal: {
      average: Number(input.averageUniqueScans),
      total: Number(input.sumUniqueScans),
      averageArrowType: input.averageUniqueScansArrowType,
      totalArrowType: input.sumUniqueScansArrowType,
    },
    allHitsAverageAndTotal: {
      average: Number(input.averageHits),
      total: Number(input.sumHits),
      averageArrowType: input.averageHitsArrowType,
      totalArrowType: input.sumHitsArrowType,
    },
    securedAverageAndTotal: {
      average: Number(input.averageSecured),
      total: Number(input.sumSecured),
      averageArrowType: input.averageSecuredArrowType,
      totalArrowType: input.sumSecuredArrowType,
    },
  };
};

interface GetDatesMissedRepoDetailsInput {
  isYtd: boolean;
  urlYtd: string;
  calendarDate: string;
  ytdCalendarDate: { startDate: moment.Moment; endDate: moment.Moment };
  timezone: string;
}
export const getDatesMissedRepoDetails = ({
  isYtd,
  urlYtd,
  calendarDate,
  ytdCalendarDate,
  timezone,
}: GetDatesMissedRepoDetailsInput) => {
  let fetchingDate;
  if (isYtd) {
    fetchingDate = getDateForCalculation(urlYtd, calendarDate, ytdCalendarDate);
  } else {
    fetchingDate = moment(calendarDate).format('YYYY-MM-DD');
  }

  const { startDate, prevStartDate, endDate, prevEndDate } = dateCalculation(
    fetchingDate,
    timezone,
    'YYYY-MM-DDTHH:mm:ssZ',
    isYtd,
  );

  return { startDate, prevStartDate, endDate, prevEndDate };
};

export const useUserAssignationData = (caseData: CaseType | null): CaseType => {
  const { caseAssignUsers } = useAppSelector((state) => state.cases);
  const caseCache = caseAssignUsers?.res?.data?.updateOne;

  const cachedCaseMatches = caseCache?.caseId === caseData?.caseId;

  const assignationData = cachedCaseMatches ? caseCache : caseData;

  return assignationData;
};

export const getTimeRangeLeaderboard = (calendarDate: string, isYtd: boolean) => {
  let startDateTime;

  if (isYtd) {
    startDateTime = DateTime.fromISO(calendarDate).startOf('year').toUTC().toISO();
  } else {
    startDateTime = DateTime.fromISO(calendarDate).startOf('month').toUTC().toISO();
  }

  const endDateTime = DateTime.fromISO(calendarDate).endOf('day').toUTC().toISO();

  return {
    startDateTime,
    endDateTime,
  };
};

export const getDateTimesRange = (dateStr: string) => {
  const startDateTime = DateTime.fromISO(dateStr).startOf('day').plus({ hours: DAILY_REPORT_HOUR });
  if (!startDateTime.isValid) {
    throw new Error('Failed to generate startDateTime ISO String from dateStr');
  }
  const startDateTimeISO = startDateTime.toUTC().toISO();

  const endDateTime = DateTime.fromISO(startDateTimeISO).plus({ days: 1 }).minus({ seconds: 1 });
  if (!endDateTime.isValid) {
    throw new Error('Failed to generate endDateTime ISO String from startDateTimeISO');
  }
  const endDateTimeISO = endDateTime.toUTC().toISO();

  const previousStartDateTime = DateTime.fromISO(startDateTimeISO).minus({ days: 1 });
  if (!previousStartDateTime.isValid) {
    throw new Error('Failed to generate previousStartDateTime ISO String from startDateTimeISO');
  }
  const previousStartDateTimeISO = previousStartDateTime.toUTC().toISO();

  const previousEndDateTime = DateTime.fromISO(endDateTimeISO).minus({ days: 1 });
  if (!previousEndDateTime.isValid) {
    throw new Error('Failed to generate previousEndDateTime ISO String from endDateTimeISO');
  }
  const previousEndDateTimeISO = previousEndDateTime.toUTC().toISO();

  return {
    startDateTime: startDateTimeISO,
    endDateTime: endDateTimeISO,
    previousStartDateTime: previousStartDateTimeISO,
    previousEndDateTime: previousEndDateTimeISO,
  };
};

export const validateName = (name: string) => {
  if (!name)
    return {
      firstName: undefined,
      lastName: undefined,
    };
  const splittedName = name?.split(' ');
  return {
    firstName: splittedName[0],
    lastName: splittedName[1] || '',
  };
};

export const displaySubtitleWithOrWithoutMonth = (text: string, month: string, dateType: string): string => {
  if (dateType === DateRangeType.MTD) {
    return `${text} - ${month}`;
  }

  return text;
};

// Change unknown lienholder|client to Title Case.
export const unknownToTitleCase = (string: string | undefined) => {
  const unknownRegex = /unknown\s+(lienholder|client)/gi;
  if (!string) return null;
  return string.replace(unknownRegex, (match) => {
    const capturedWord = match.toLowerCase();
    const splittedWord = capturedWord.split(' ');
    const unknown = _.capitalize(splittedWord[0]);
    const clientOrLienholder = _.capitalize(splittedWord[1]);
    return unknown + ' ' + clientOrLienholder;
  });
};

export const getSubTitle = (daySelected: DayMenuOption) => {
  if (daySelected === DayMenuOption.TODAY) {
    return 'Yesterday';
  }
  return 'Previous Day';
};

export const formattedInput = (
  branches: string[] | number[],
  dateRanges: TargetRecoveryRateDuration[] | string[],
  orderTypes: (string | null)[],
  targetRecoveryRate: number,
  userId: number,
  clientId?: string | null,
  lienholderId?: string,
) => {
  const formatted = dateRanges.flatMap((dateRange) =>
    branches.flatMap((branchId) =>
      !orderTypes?.length
        ? [
            {
              clientId,
              userId,
              lienholderId,
              branchId: branchId === '0' ? null : branchId,
              duration: dateRange,
              targetRecoveryRate,
            },
          ]
        : orderTypes.map((orderType) => ({
            clientId,
            userId,
            lienholderId,
            orderType: orderType === OrderTypeCategory.ALL ? null : orderType,
            branchId: branchId === '0' ? null : branchId,
            duration: dateRange,
            targetRecoveryRate,
          })),
    ),
  );

  return formatted;
};

export function getSpottedNotSecuredTitle(status?: CaseStatusFilter) {
  if (status === CaseStatusFilter.OPEN) return CaseStatus.OPEN;
  if (status === CaseStatusFilter.CLOSED_HOLD) return `${CaseStatus.CLOSED}/${CaseStatus.ON_HOLD}`;

  return ``;
}
